import React, { useCallback, useMemo } from 'react';
import Slider from 'rc-slider';
import { set } from 'lodash/fp';

import { Button } from '../../shared/button/Button';
import { PositionModal, PositionModalProps } from '../../shared/modal/PositionModal';
import { Toggle } from '../../shared/toggle';
import styles from './DocumentViewer.module.scss';
import { PDFSearchType } from './store';
import { Settings } from '../../shared/icons';
import { ModalHeader } from '../../shared/modal/ModalHeader';

const { french, grey, fixedWhite } = styles;

interface MarkerProps {
    type: string;
}

const Marker: React.FC<MarkerProps> = ({ type }) => (
    <div className={styles.searchTypeMarker}>
        {type}
    </div>
);

interface PdfSearchModalProps extends PositionModalProps {
    toggleScheduleOnly: () => void;
    applyChanges: () => void;
    changeSearchType: (type: PDFSearchType) => void;
    searchType: PDFSearchType;
    scheduleOnly: boolean;
    showScheduleStartPage: boolean;
    canApplyChanges: boolean;
}

export const PdfSearchModal: React.FC<PdfSearchModalProps> = ({ position, closeModal, isOpen, testId, toggleScheduleOnly, applyChanges, changeSearchType, searchType, scheduleOnly, showScheduleStartPage, canApplyChanges }) => {
    const searchOptions = useMemo(() => [PDFSearchType.EXACT, PDFSearchType.FUZZY_2, PDFSearchType.FUZZY_3, PDFSearchType.FUZZY_4, PDFSearchType.FUZZY_5], []);
    const getTypeMarkerLabel = useCallback((type: PDFSearchType) => {
        if (type === PDFSearchType.EXACT) {
            return 'Exact';
        }
        if (type === PDFSearchType.FUZZY_5) {
            return 'Fuzzy';
        }
        return '';
    }, []);
    const searchMarkers = useMemo(() => searchOptions.reduce((acc, type, index) => set(index, <Marker type={getTypeMarkerLabel(type)} />, acc), {}), [searchOptions, getTypeMarkerLabel]);
    const currentlySelected = useMemo(() => searchOptions.findIndex(option => option === searchType), [searchOptions, searchType]);
    const selectSearchType = useCallback((index: number | number[]) => changeSearchType(searchOptions[index as number]), [searchOptions, changeSearchType]);

    return (
        <PositionModal
            isOpen={isOpen}
            closeModal={closeModal}
            position={position}
            testId={testId}
            height='fit-content'
            renderLeftOfClick
            width='320px'
            overflow='none'
        >
            <div className={styles.searchSettingsWrapper}>
                <ModalHeader icon={Settings} label='Search Settings' iconSize={20} overflowTooltipClassName={styles.searchHeader} marginBottom='5px' />
                <div className={styles.settingsSlider}>
                    <div className={styles.sliderLabel}>Accuracy</div>
                    <div className={styles.sliderWrapper}>
                        <Slider
                            min={0}
                            marks={searchMarkers}
                            onChange={selectSearchType}
                            defaultValue={0}
                            value={currentlySelected}
                            max={4}
                            step={1}
                            trackStyle={{ backgroundColor: grey }}
                            railStyle={{ backgroundColor: grey }}
                            handleStyle={{ backgroundColor: french, border: `1px solid ${french}`, height: '16px', width: '16px', marginTop: '-6px', opacity: 1 }}
                            dotStyle={{ backgroundColor: fixedWhite, border: `1px solid ${french}` }}
                        />
                    </div>
                </div>
                {showScheduleStartPage &&
                    <div className={styles.scheduleOnlyWrapper}>
                        <div className={styles.scheduleLabel}>Schedule Only</div>
                        <Toggle onChange={toggleScheduleOnly} checked={scheduleOnly} />
                    </div>
                }
                <div className={styles.buttonsWrapper}>
                    <Button width='100px' label='Cancel' onClick={closeModal} />
                    <Button width='100px' label='Apply' onClick={applyChanges} disabled={!canApplyChanges} />
                </div>
            </div>
        </PositionModal>
    );
};
